import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Navigate, Link } from 'react-router-dom';
import { BarChart3, Users, Shield, Zap } from 'lucide-react';
import { AppHeader } from '@/components/layout/AppHeader';
import { useTranslation } from 'react-i18next';

const Index = () => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <span className="ltr:ml-2 rtl:mr-2">{t('common.loading')}</span>
      </div>
    );
  }

  // Redirect authenticated users to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader showNavigation={false} />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20 text-center">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          {t('homepage.heroTitle')}
        </h2>
        
        {/* Professional Statistical Visualization */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md lg:w-80 h-24 sm:h-28 md:h-32 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20 p-3 sm:p-4">
            <div className="flex items-end justify-between h-full gap-1">
              {/* Statistical bars representing data analysis */}
              <div className="flex items-end gap-1 h-full">
                <div className="w-3 bg-primary/70 rounded-t" style={{height: '60%'}}></div>
                <div className="w-3 bg-primary/80 rounded-t" style={{height: '85%'}}></div>
                <div className="w-3 bg-primary rounded-t" style={{height: '45%'}}></div>
                <div className="w-3 bg-primary/90 rounded-t" style={{height: '92%'}}></div>
                <div className="w-3 bg-primary/75 rounded-t" style={{height: '70%'}}></div>
              </div>
              
              {/* Statistical trend line */}
              <div className="flex-1 relative ml-4">
                <svg className="w-full h-full" viewBox="0 0 100 40">
                  <path
                    d="M5,35 Q25,25 45,20 T85,15"
                    stroke="hsl(var(--primary))"
                    strokeWidth="2"
                    fill="none"
                    className="drop-shadow-sm"
                  />
                  <circle cx="5" cy="35" r="2" fill="hsl(var(--primary))" />
                  <circle cx="25" cy="25" r="2" fill="hsl(var(--primary))" />
                  <circle cx="45" cy="20" r="2" fill="hsl(var(--primary))" />
                  <circle cx="65" cy="18" r="2" fill="hsl(var(--primary))" />
                  <circle cx="85" cy="15" r="2" fill="hsl(var(--primary))" />
                </svg>
              </div>
              
              {/* Statistical symbols */}
              <div className="absolute top-1 right-2 text-xs text-primary/60 font-mono">
                σ² α β
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-3xl mx-auto px-2">
          {t('homepage.heroDescription')}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
          <Button size="lg" className="w-full sm:w-auto min-w-0 sm:min-w-[200px]" asChild>
            <Link to="/auth?tab=signup">{t('homepage.findStatistician')}</Link>
          </Button>
          <Button size="lg" variant="outline" className="w-full sm:w-auto min-w-0 sm:min-w-[200px]" asChild>
            <Link to="/auth?tab=signup">{t('homepage.offerExpertise')}</Link>
          </Button>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20">
        <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12">{t('homepage.whyChoose')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center ltr:gap-4 rtl:gap-4 rtl:flex-row-reverse">
                <Users className="h-12 w-12 text-primary flex-shrink-0" />
                <CardTitle className="flex-1">{t('homepage.expertNetwork')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                {t('homepage.expertNetworkDesc')}
              </CardDescription>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center ltr:gap-4 rtl:gap-4 rtl:flex-row-reverse">
                <Shield className="h-12 w-12 text-primary flex-shrink-0" />
                <CardTitle className="flex-1">{t('homepage.securePlatform')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                {t('homepage.securePlatformDesc')}
              </CardDescription>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center ltr:gap-4 rtl:gap-4 rtl:flex-row-reverse">
                <Zap className="h-12 w-12 text-primary flex-shrink-0" />
                <CardTitle className="flex-1">{t('homepage.fastMatching')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                {t('homepage.fastMatchingDesc')}
              </CardDescription>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center ltr:gap-4 rtl:gap-4 rtl:flex-row-reverse">
                <BarChart3 className="h-12 w-12 text-primary flex-shrink-0" />
                <CardTitle className="flex-1">{t('homepage.qualityResults')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                {t('homepage.qualityResultsDesc')}
              </CardDescription>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-muted py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">{t('homepage.readyToStart')}</h3>
          <p className="text-lg sm:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
            {t('homepage.joinThousands')}
          </p>
          <Button size="lg" className="w-full sm:w-auto" asChild>
            <Link to="/auth?tab=signup">{t('homepage.createAccount')}</Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-6 sm:py-8 border-t">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p className="text-muted-foreground text-sm">
              Copyright © 2025 <span className="font-semibold">StatConsult</span>. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-2 sm:gap-3 text-sm">
              <Link to="/about" className="text-primary hover:underline whitespace-nowrap">
                About Us
              </Link>
              <Link to="/contact" className="text-primary hover:underline">
                Contact
              </Link>
              <Link to="/faq" className="text-primary hover:underline">
                FAQ
              </Link>
              <Link to="/terms" className="text-primary hover:underline">
                Terms
              </Link>
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy
              </Link>
              <Link to="/platform-usage" className="text-primary hover:underline">
                Usage Policy
              </Link>
            </div>
          </div>
          <p className="text-muted-foreground text-sm">
            Developed and Operated by <span className="font-semibold">HMT Solutions</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
